import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import photoboothService from "../services/photoboothService";
import getOptimizedUrl from "../utils/imageUtils";
import HomeMobileNavbar from "../components/HomeMobileNavbar";
import Navbar from "../components/Navbar";

const PhotoBooth = () => {
  const [windowWidth, setWindowWidth] = React.useState(
    typeof window !== "undefined" ? window.innerWidth : 1200,
  );

  React.useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  const [photos, setPhotos] = useState([]);
  const [selected, setSelected] = useState(null);
  const [settings, setSettings] = useState(null);

  const isPhoneView = windowWidth < 768;

  useEffect(() => {
    const load = async () => {
      try {
        const [p, s] = await Promise.all([
          photoboothService.getPhotos(),
          photoboothService.getSettings(),
        ]);

        const sortedPhotos = (p || []).sort(
          (a, b) => a.sortOrder - b.sortOrder,
        );

        setPhotos(sortedPhotos);
        setSettings(s || null);

        if (
          sortedPhotos.length > 0 &&
          (typeof window === "undefined" || window.innerWidth >= 768)
        ) {
          setSelected(sortedPhotos[0]);
        }
      } catch (err) {
        console.error("Failed to load photobooth", err);
      }
    };
    load();
  }, []);

  useEffect(() => {
    if (!isPhoneView && !selected && photos.length > 0) {
      setSelected(photos[0]);
    }
  }, [isPhoneView, selected, photos]);

  const getDisplayQuote = (photo) =>
    photo?.alt ? `“${photo.alt}”` : "";

  const handlePhotoClick = (photo) => {
    if (isPhoneView && selected?._id === photo._id) {
      setSelected(null);
      return;
    }
    setSelected(photo);
  };

  if (!selected && photos.length === 0) return null;

  return (
    <div>
      {windowWidth < 1168 ? <HomeMobileNavbar /> : <Navbar />}
      <div className="w-full max-w-screen mx-auto px-4 md:px-6 py-8 md:py-12 font-bdogrotesk text-black">
        {/* --- TOP SECTION --- */}
        {!isPhoneView && selected && (
          <div className="flex flex-col justify-between lg:flex-row gap-8 lg:gap-24 lg:min-h-[550px] mb-12 lg:mb-20">
          <div className="w-full lg:w-2/3 flex flex-col justify-between py-0 lg:py-2 order-2 lg:order-1">
            <div>
              <h3 className="text-sm font-medium mb-4 lg:mb-6">Photo Album</h3>
            </div>
            <div className="mt-6 lg:mt-8 space-y-5">
              {getDisplayQuote(selected) ? (
                <h1 className="text-xl sm:text-2xl md:text-5xl lg:text-6xl font-medium leading-[1.1] tracking-tight">
                  {getDisplayQuote(selected)}
                </h1>
              ) : null}
              <p
                className="text-sm uppercase tracking-widest font-bold"
                style={{ fontFamily: '"Courier Prime", Courier, monospace' }}
              >
                {selected?.modelName || "NO NAME AVAILABLE"}
              </p>
            </div>
          </div>

          <div className="w-full lg:pl-8 lg:w-1/3 order-1 lg:order-2">
            <div className="w-full aspect-[3/4] max-h-[78vh]  overflow-hidden relative">
              <img
                src={getOptimizedUrl(selected?.imageUrl || "", 1200)}
                alt={selected?.alt || selected?.garmentName}
                className="w-full h-full object-cover"
              />
              <div className="absolute bottom-4 right-4 text-xs font-bold uppercase bg-white px-2 py-1"></div>
            </div>
            <div className="flex justify-between items-center mt-3 text-xs md:text-sm font-mono text-gray-500 uppercase">
              <span className="shrink-0">
                {String(photos.indexOf(selected) + 1).padStart(2, "0")} /{" "}
                {String(photos.length).padStart(2, "0")}
              </span>
              {selected?.product?.slug && (
                <Link
                  to={`/product/${selected.product.slug}`}
                  onClick={(e) => e.stopPropagation()}
                  style={{ fontFamily: '"Courier Prime", Courier, monospace' }}
                  className="truncate ml-3 max-w-[60%]"
                >
                  {selected.product.product}
                </Link>
              )}
            </div>
          </div>
          </div>
        )}

        {/* --- BOTTOM SECTION: FLEXBOX LAYOUT --- */}
        <div className="w-full">
          {isPhoneView ? (
            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              {photos.map((photo, index) => {
                const isSelected = selected?._id === photo._id;

                return (
                  <div
                    key={photo._id}
                    onClick={() => handlePhotoClick(photo)}
                    className={`cursor-pointer transition-all duration-300 ease-in-out ${
                      isSelected ? "col-span-2" : "col-span-1"
                    }`}
                  >
                    <div className="text-xs font-mono text-gray-400 mb-2">
                      {String(index + 1).padStart(2, "0")}
                    </div>

                    {isSelected ? (
                      <div className="space-y-4">
                        <div className="w-full aspect-[3/4] overflow-hidden relative">
                          <img
                            src={getOptimizedUrl(photo.imageUrl || "", 1200)}
                            alt={photo.alt || photo.garmentName}
                            className="w-full h-full object-cover"
                          />
                        </div>

                        <div className="space-y-3 pb-2">
                          <div className="flex justify-between items-center text-xs font-mono text-gray-500 uppercase">
                            <span className="shrink-0">
                              {String(index + 1).padStart(2, "0")} /{" "}
                              {String(photos.length).padStart(2, "0")}
                            </span>
                            {photo?.product?.slug && (
                              <Link
                                to={`/product/${photo.product.slug}`}
                                onClick={(e) => e.stopPropagation()}
                                style={{ fontFamily: '"Courier Prime", Courier, monospace' }}
                                className="truncate ml-3 max-w-[60%]"
                              >
                                {photo.product.product}
                              </Link>
                            )}
                          </div>

                          {getDisplayQuote(photo) ? (
                            <h1 className="text-lg sm:text-xl font-medium leading-[1.2] tracking-tight">
                              {getDisplayQuote(photo)}
                            </h1>
                          ) : null}
                          <p
                            className="text-xs uppercase tracking-widest font-bold"
                            style={{ fontFamily: '"Courier Prime", Courier, monospace' }}
                          >
                            {photo?.modelName || "NO NAME AVAILABLE"}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div className="w-full overflow-hidden h-36 sm:h-40 relative">
                        <img
                          src={getOptimizedUrl(photo.imageUrl || "", 800)}
                          alt={photo.alt || photo.garmentName}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-wrap gap-4">
              {photos.map((photo, index) => {
                const isSelected = selected?._id === photo._id;

                return (
                  <div
                    key={photo._id}
                    onClick={() => handlePhotoClick(photo)}
                    className={`
                    flex flex-col cursor-pointer group transition-all duration-300 ease-in-out
                    ${
                      isSelected
                        ? "w-[calc(62%-8px)] sm:w-[calc(45%-12px)] md:w-[calc(16%-14.4px)]"
                        : "w-[calc(38%-8px)] sm:w-[calc(27.5%-12px)] md:w-[calc(8%-14.4px)]"
                    }
                  `}
                  >
                    <div className="text-xs font-mono text-gray-400 mb-2">
                      {String(index + 1).padStart(2, "0")}
                    </div>

                    <div
                      className={`w-full overflow-hidden mb-3 h-36 sm:h-32 md:h-40 relative transform-gpu origin-center transition-transform duration-300 ease-in-out ${
                        isSelected ? "md:scale-x-110" : "md:scale-x-100"
                      }`}
                    >
                      <img
                        src={getOptimizedUrl(photo.imageUrl || "", 800)}
                        alt={photo.alt || photo.garmentName}
                        className="w-full h-full object-cover transition-transform duration-700"
                      />
                      {isSelected && (
                        <div className="absolute inset-0 bg-black/5 pointer-events-none"></div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PhotoBooth;
